import { useCallback, useEffect, useMemo, useRef } from "react";
import useLocalStorage from "@scripts/react/hooks/useLocalStorage";
import { useAuth } from "@rapid/contexts/AuthContext";
import {
    useWorkspaces,
    useApiKey,
    useClearWorkspaceQueries
} from "@rapid/queries/workspaceQueries";
import { migrateFromZustandStore } from "@rapid/utils/migrateFromZustandStore";
import type { Workspace } from "@rapid/types/workspace";
import { WorkspaceContext, WorkspaceContextValue } from "@rapid/contexts/WorkspaceContext";

const STORAGE_KEY = "rapid-current-workspace";
migrateFromZustandStore(STORAGE_KEY, "currentWorkspace");

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const { data } = useWorkspaces();
    const workspaces = data?.workspaces;

    const [currentWorkspaceState, setCurrentWorkspace] = useLocalStorage<Workspace | null>(
        STORAGE_KEY,
        null
    );

    // Clear workspace state on logout (user transitions from set → null)
    const clearWorkspaceQueries = useClearWorkspaceQueries();
    const prevUserRef = useRef(user);
    useEffect(() => {
        if (prevUserRef.current && !user) {
            setCurrentWorkspace(null);
            clearWorkspaceQueries();
        }
        prevUserRef.current = user;
    }, [user, setCurrentWorkspace, clearWorkspaceQueries]);

    // Auto-select workspace when workspaces arrive
    useEffect(() => {
        if (!workspaces || workspaces.length === 0) return;

        if (currentWorkspaceState) {
            if (workspaces.some((w: Workspace) => w.id === currentWorkspaceState.id)) return;
        }

        const personalWorkspace = workspaces.find((w) => w.id === user?.uid);
        setCurrentWorkspace(personalWorkspace ?? workspaces[0]);
    }, [workspaces, currentWorkspaceState, user?.uid, setCurrentWorkspace]);

    const switchWorkspace = useCallback(
        (id: string) => {
            const ws = workspaces?.find((w) => w.id === id);
            if (ws) setCurrentWorkspace(ws);
        },
        [workspaces, setCurrentWorkspace]
    );

    const currentWorkspaceUsage = useMemo(
        () => data?.usages?.find((u) => u.id === currentWorkspaceState?.id) ?? null,
        [data?.usages, currentWorkspaceState?.id]
    );

    const { data: apiKey } = useApiKey(currentWorkspaceState?.id);

    const value = useMemo(
        (): WorkspaceContextValue => ({
            currentWorkspace: currentWorkspaceState,
            currentWorkspaceUsage,
            apiKey: apiKey ?? null,
            switchWorkspace
        }),
        [currentWorkspaceState, currentWorkspaceUsage, apiKey, switchWorkspace]
    );

    return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
